import { FC, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';

import DataTable from 'components/tables/data-table';
import { UnitNumber } from 'components/tables/utils';
import { downloadBillableEventsWithSpecification, fetchBillableEventsWithSpecification } from 'services';
import { BillableEvent, BillableEventType } from 'types';
import { T_DataTableColumn } from 'types/data-table';

export class TransactionBillableEventsColumns {
  timestamp: T_DataTableColumn<BillableEvent, 'dateTime'> = {
    field: 'timestamp',
    label: 'DATE',
    type: 'dateTime',
    dateFormat: 'M/D/YY LT'
  };
  unit: T_DataTableColumn<BillableEvent, 'unit'> = {
    field: 'unit',
    label: 'PRIMARY UNIT',
    type: 'unit',
    getValueLabel: (unit, billableEvent) => unit?.unitNumber ?? billableEvent.unitNumber,
    renderCell: unit => <UnitNumber unit={unit} ADMIN BILLING_ADMIN BOOKING CUSTOMER EXTERNAL />
  };
  type: T_DataTableColumn<BillableEvent, 'eventType'> = {
    field: 'type',
    label: 'DETAILS',
    type: 'eventType',
    getValueLabel: (eventType, billableEvent) => {
      switch (eventType) {
        case BillableEventType.LIFT_OFF:
          return 'LIFTED OFF';
        case BillableEventType.LIFT_ON:
          return 'LIFTED ON';
        case BillableEventType.DIG:
          return 'DIG';
        case BillableEventType.CREDIT:
          return 'CREDIT';
        case BillableEventType.OTHER:
        default:
          return billableEvent.label;
      }
    }
  };
  associatedUnit: T_DataTableColumn<BillableEvent, 'unit'> = {
    field: 'associatedUnit',
    label: 'ASSOCIATED UNIT',
    type: 'unit',
    getValueLabel: associatedUnit => associatedUnit?.unitNumber,
    renderCell: associatedUnit => <UnitNumber unit={associatedUnit} ADMIN BILLING_ADMIN BOOKING CUSTOMER EXTERNAL />
  };
}

const TransactionEvents: FC = () => {
  const { transactionId } = useParams();
  const billableEventColumns = useRef(new TransactionBillableEventsColumns());

  const fetchData = useMemo(() => fetchBillableEventsWithSpecification({ transactionId }), [transactionId]);

  return (
    <div className="card-primary">
      <div className="card-header px-3">
        <h4>BILLABLE EVENTS</h4>
      </div>
      <div className="card-body">
        <DataTable
          columns={Object.values(billableEventColumns.current)}
          fetchCsv={downloadBillableEventsWithSpecification({ transactionId })}
          fetchData={fetchData}
          initialColumns={Object.keys(billableEventColumns.current).map(c => {
            return { field: c, width: 80 };
          })}
        />
      </div>
    </div>
  );
};

export default TransactionEvents;
